import { api as apiConfig } from '@/config/config';
import { CognitoService } from '@/services/cognito';
import router from '@/router';
import store from '@/store';
import { AddressErrors } from '@/data/static/addressErrors';

function toQuerySearchParams(params) {
  if (!params) return '';
  const querySearchParams = new URLSearchParams(params);
  return querySearchParams;
}

function toURL({ url, params = null, apiVersion = 'v1' }) {
  const sanitizedUrl = url[0] === '/' ? url.substring(1) : url;
  const { baseUrl, baseUrlV2 } = apiConfig;
  const apiVersionPathMap = {
    v1: sanitizedUrl,
    v2: `v2/${sanitizedUrl}`,
  };
  const apiVersionUrlMap = {
    v1: baseUrl,
    v2: baseUrlV2,
  };
  const urlObj = new URL(apiVersionPathMap[apiVersion], apiVersionUrlMap[apiVersion]);
  const querySearchParams = toQuerySearchParams(params);
  if (!querySearchParams) return urlObj;
  urlObj.search = querySearchParams;
  return urlObj;
}

const makeRequest = async (url, { method, headers = {}, body = null } = {}, legacyToken) => {
  const token = legacyToken || await CognitoService.getIdToken();
  // eslint-disable-next-line no-param-reassign
  headers.Authorization = `Bearer ${token}`;

  return fetch(url, { method, headers, body })
    .then(async (response) => {
      const contentType = response.headers.get('content-type');
      let responseBody;

      if (contentType && contentType.includes('application/json')) {
        responseBody = await response.json();
      }

      if (responseBody && responseBody.suggestedAddress) {
        const { addressType, suggestedAddress } = responseBody;

        if (suggestedAddress) {
          store.commit('accountCreation/SET_SUGGESTED_ADDRESS', suggestedAddress);
          store.commit('accountCreation/SET_SUGGESTED_ADDRESS_TYPE', addressType);
          store.commit('accountCreation/SET_SUGGESTED_ADDRESS_MODAL_VISIBLE', true);
          store.commit('accountCreation/SET_SUGGESTED_ADDRESS_YES_METHOD', () => {
            store.commit('accountCreation/SET_SUGGESTED_ADDRESS_YES_CALL', {
              payload: JSON.parse(body),
              suggestedAddress,
              addressType,
            });
          });
        }
      }

      if (responseBody && responseBody.errorMessage) {
        // Admins can override a commercial residential address. For them we let
        // the calling screen show a dedicated override modal instead of the
        // generic error modal. Everyone else (e.g. investors self-editing) still
        // gets the plain "not allowed" message here.
        const isAdminCommercialOverride =
          responseBody.errorMessage === 'RESIDENTIAL_COMMERCIAL_ADDRESS_NOT_ALLOWED'
          && store.state.app && store.state.app.irAdmin;

        if (!isAdminCommercialOverride) {
          const error = AddressErrors[responseBody.errorMessage] || responseBody.errorMessage;
          store.commit('errors/SET_ERRORS_MODAL_INFO', [error]);
          store.commit('errors/SHOW_ERRORS_MODAL');
        }
      }

      switch (response.status) {
        case 401:
          router.replace({ name: 'Login' });
          return Promise.reject('UNAUTHORIZED');
        case 403:
          router.replace({ name: 'Home' });
          return Promise.reject('FORBIDDEN');
        case 204:
          return Promise.resolve();
        default:
          if (!contentType) {
            return Promise.resolve();
          }
          if (
            contentType.includes('text/csv') ||
            contentType.includes(
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            )
          ) {
            return response.blob();
          }
          if (!response.ok) {
            return Promise.reject(responseBody);
          }
          return responseBody;
      }
    })
    .catch(error => Promise.reject(error));
};

const doRequest = (method, urlObj, data, options = {}) => makeRequest(
  urlObj.href,
  {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(data),
  },
  options.legacyToken,
);

const doDelete = (urlObj, data, options = {}) =>
  makeRequest(urlObj.href, {
    method: 'DELETE',
    headers: {
      ...options.headers,
    },
    ...(data
      ? {
        headers: {
          ...options.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
      : {}),
  });

const doUploadFile = (urlObj, file, data = {}, options = {}) => {
  const body = new FormData();
  body.append('file', file);
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });
  return makeRequest(urlObj.href, {
    method: 'POST',
    headers: options.headers,
    body,
  });
};
const doUploadFiles = (urlObj, files, data = {}, options = {}) => {
  const body = new FormData();
  files.forEach((file) => {
    body.append('files', file);
  });
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });
  return makeRequest(urlObj.href, {
    method: 'POST',
    headers: options.headers,
    body,
  });
};

const doUploadFilesAndForm = (urlObj, data, files) => {
  const body = new FormData();
  files.forEach((file) => {
    body.append('file', file);
  });
  body.append('filesCount', files.length);
  Object.keys(data).forEach((key) => {
    if (key === 'emails') {
      body.append(key, JSON.stringify(data[key]));
    } else {
      body.append(key, data[key]);
    }
  });
  return makeRequest(urlObj.href, {
    method: 'POST',
    body,
  });
};

class HttpService {
  static post(url, data, options = {}) {
    const urlObj = toURL({ url });
    return doRequest('POST', urlObj, data, options);
  }
  static postV2(url, data, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doRequest('POST', urlObj, data, options);
  }
  static put(url, data, options = {}) {
    const urlObj = toURL({ url });
    return doRequest('PUT', urlObj, data, options);
  }
  static putV2(url, data, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doRequest('PUT', urlObj, data, options);
  }
  static patchV2(url, data, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doRequest('PATCH', urlObj, data, options);
  }
  static get(url, params, options = {}) {
    const urlObj = toURL({ url, params });
    return makeRequest(urlObj.href, {
      method: 'GET',
      headers: options.headers,
    });
  }
  static getV2(url, params, options = {}) {
    const urlObj = toURL({ url, params, apiVersion: 'v2' });
    return makeRequest(urlObj.href, {
      method: 'GET',
      headers: options.headers,
    });
  }
  static delete(url, data, options = {}) {
    const urlObj = toURL({ url });
    return doDelete(urlObj, data, options);
  }
  static deleteV2(url, data, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doDelete(urlObj, data, options);
  }
  static uploadFile(url, file, data = {}, options = {}) {
    const urlObj = toURL({ url });
    return doUploadFile(urlObj, file, data, options);
  }
  static uploadFileV2(url, file, data = {}, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doUploadFile(urlObj, file, data, options);
  }
  static uploadFilesV2(url, files, data = {}, options = {}) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doUploadFiles(urlObj, files, data, options);
  }
  static uploadFilesAndForm(url, data, files) {
    const urlObj = toURL({ url });
    return doUploadFilesAndForm(urlObj, data, files);
  }
  static uploadFilesAndFormV2(url, data, files) {
    const urlObj = toURL({ url, apiVersion: 'v2' });
    return doUploadFilesAndForm(urlObj, data, files);
  }

  static getMfaOptions(email) {
    const urlObj = toURL({ url: 'auth/mfa/options', params: { email }, apiVersion: 'v2' });
    return fetch(urlObj.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to get MFA options');
        }
        return response.json();
      });
  }
}

export default HttpService;



// WEBPACK FOOTER //
// ./src/api/HttpService.js